import { useCallback, useEffect, useState } from "react";

export const DRAWER_MIN_WIDTH_PX = 450;

export const DRAWER_MAX_WIDTH_FRACTION = 0.8;

/** Initial drawer width when opened (viewport fraction, then clamped). */
export const DRAWER_DEFAULT_WIDTH_FRACTION = 0.65;

/**
 * Layout width used for clamping drawer width (prefers Visual Viewport API on mobile Safari).
 * @returns {number} Width in CSS pixels; SSR fallback `1024` when `window` is undefined.
 */
function getViewportWidthPx() {
    if (typeof window === "undefined") {
        return 1024;
    }
    return window.visualViewport?.width ?? window.innerWidth;
}

/**
 * Clamps a desired drawer width to {@link DRAWER_MIN_WIDTH_PX} and a viewport-based maximum.
 * @param {number} px Raw width in pixels.
 * @returns {number} Rounded, clamped width in pixels.
 */
export function clampDrawerWidthPx(px) {
    if (typeof window === "undefined") {
        return px;
    }
    const vw = getViewportWidthPx();
    const max = Math.floor(vw * DRAWER_MAX_WIDTH_FRACTION);
    return Math.min(
        max,
        Math.max(DRAWER_MIN_WIDTH_PX, Math.round(px)),
    );
}

/**
 * Default drawer width on open: {@link DRAWER_DEFAULT_WIDTH_FRACTION} of the viewport, then clamped.
 * @returns {number} Width in pixels; SSR fallback `780` when `window` is undefined.
 */
export function defaultDrawerWidthPx() {
    if (typeof window === "undefined") {
        return 780;
    }
    const vw = getViewportWidthPx();
    const w = Math.round(vw * DRAWER_DEFAULT_WIDTH_FRACTION);
    return clampDrawerWidthPx(w);
}

/**
 * Drawer width state and a `pointerdown` handler for the left-edge resize control on a
 * right-docked drawer. Drag uses window-level pointer listeners scoped by `pointerId`
 * (no pointer capture, for compatibility with Radix dismiss layers). Width updates during
 * drag are batched with `requestAnimationFrame`. A window `blur` listener ends the gesture
 * if focus leaves the page mid-drag. Max width is capped at {@link DRAWER_MAX_WIDTH_FRACTION}
 * of the viewport; while `isOpen`, resize events re-clamp the stored width.
 *
 * @param {boolean} isOpen When false, viewport listeners from this hook are not attached.
 * @returns {{
 *   widthPx: number,
 *   onResizePointerDown: (event: PointerEvent) => void
 * }}
 */
export function useResizableDrawerWidth(isOpen) {
    const [widthPx, setWidthPx] = useState(defaultDrawerWidthPx);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const onViewportChange = () => {
            setWidthPx((w) => clampDrawerWidthPx(w));
        };
        window.addEventListener("resize", onViewportChange);
        window.visualViewport?.addEventListener("resize", onViewportChange);
        return () => {
            window.removeEventListener("resize", onViewportChange);
            window.visualViewport?.removeEventListener(
                "resize",
                onViewportChange,
            );
        };
    }, [isOpen]);

    const onResizePointerDown = useCallback((event) => {
        if (event.pointerType === "mouse" && event.button !== 0) {
            return;
        }
        // Avoid selecting page text while dragging the resize handle.
        event.preventDefault();

        const pointerId = event.pointerId;
        const body = document.body;
        const previousUserSelect = body.style.userSelect;
        body.style.userSelect = "none";

        let cleanedUp = false;
        let rafId = null;
        let pendingWidthPx = null;

        const flushPending = () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
            if (pendingWidthPx !== null) {
                setWidthPx(pendingWidthPx);
                pendingWidthPx = null;
            }
        };

        const cleanup = () => {
            if (cleanedUp) {
                return;
            }
            cleanedUp = true;
            flushPending();
            body.style.userSelect = previousUserSelect;
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", onPointerUpOrCancel);
            window.removeEventListener("pointercancel", onPointerUpOrCancel);
            window.removeEventListener("blur", onWindowBlur);
        };

        const onPointerMove = (e) => {
            if (e.pointerId !== pointerId) {
                return;
            }
            const vw = getViewportWidthPx();
            pendingWidthPx = clampDrawerWidthPx(vw - e.clientX);
            if (rafId === null) {
                rafId = requestAnimationFrame(() => {
                    rafId = null;
                    if (pendingWidthPx !== null) {
                        setWidthPx(pendingWidthPx);
                    }
                });
            }
        };

        const onPointerUpOrCancel = (e) => {
            if (e.pointerId !== pointerId) {
                return;
            }
            cleanup();
        };

        // If the tab/window loses focus mid-drag, pointerup may not fire.
        const onWindowBlur = () => {
            cleanup();
        };

        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUpOrCancel);
        window.addEventListener("pointercancel", onPointerUpOrCancel);
        window.addEventListener("blur", onWindowBlur);
    }, []);

    return { widthPx, onResizePointerDown };
}
